import {
  ArticleEditorState,
  ArticleFormValues,
  BackendContentBlock,
  BlogSingleResponse,
  SaveStatus,
} from 'Models'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBlogRecovery } from './useBlogRecovery'
import blogService from 'Services/blogService'
import {
  buildCreateDraftPayload,
  buildUnpublishPayload,
  buildUpdatePayload,
  hasPayloadChanges,
} from 'Adapters/BlogPayloadAdapter'
import { fingerprintArticleValues } from 'Helpers/Blog/blogForm'
import { blogResponseToForm } from 'Adapters/BlogResponseAdapter'
import { validateBlocks } from 'Adapters/TipTapAdapter'

const DEBOUNCE_MS = 2000
const MAX_INTERVAL_MS = 20000

export interface ConflictState {
  /** The server's current article, fetched when the 409 was detected. */
  latestArticle: BlogSingleResponse
}

export interface UseBlogAutosaveOptions {
  /** Slug from the route, or null when the editor opened on "new article". */
  existingSlug: string | null
  /** Current article form values (already picked down to ArticleFormValues). */
  values: ArticleFormValues
  /** Public display name for `author`. Optional server-side; omit until your profile is wired in. */
  author?: string
  /** Blocks ALL saves while true (article still loading, unsupported blocks not yet acknowledged). */
  paused?: boolean
  /**
   * Set false to stop the timers (debounce + max interval) while manual saves
   * still work. Use it for PUBLISHED articles: the backend applies edits to a
   * published article immediately, so timer-driven saves would push
   * half-finished edits live.
   */
  autoSaveEnabled?: boolean
  /** Opt in to timer-driven saves on a published article (edits go live immediately). Default false. */
  autoSavePublished?: boolean
  /** Called once after the first successful create. */
  onCreated?: (slug: string) => void
  /** Called for failures the admin should see as a toast (manual save, publish, conflict). Routine autosave failures only update the inline indicator. */
  onError?: (message: string) => void
  onSuccess?: (message: string) => void
}

type SaveOutcome = { ok: true; response: BlogSingleResponse | null } | { ok: false }

const getHttpStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } }).response?.status

const getErrorMessage = (error: unknown, fallback: string): string => {
  const e = error as {
    response?: { data?: { message?: string | string[]; error?: string } }
    message?: string
  }
  const message = e.response?.data?.message
  if (Array.isArray(message)) return message.join(' ')
  return message || e.response?.data?.error || e.message || fallback
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Save lifecycle for the blog editor: debounced + max-interval autosave, one
 * in-flight request at a time, a monotonically increasing change counter so
 * edits made during a request are never lost, create-then-update, publish /
 * unpublish, and 409 conflict handling.
 *
 * Everything the save loop reads synchronously lives in refs, because timers
 * created in one render would otherwise close over stale state.
 */
export const useBlogAutosave = ({
  existingSlug,
  values,
  author,
  paused = false,
  autoSaveEnabled = true,
  autoSavePublished = false,
  onCreated,
  onError,
  onSuccess,
}: UseBlogAutosaveOptions) => {
  const [editorState, setEditorState] = useState<ArticleEditorState>({
    slug: existingSlug,
    version: null,
    publishedStatus: null,
    createdAt: null,
    updatedAt: null,
  })
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ state: 'idle' })
  const [conflict, setConflict] = useState<ConflictState | null>(null)

  const recovery = useBlogRecovery(editorState.slug)

  const valuesRef = useRef(values)
  valuesRef.current = values
  const editorStateRef = useRef(editorState)
  editorStateRef.current = editorState
  const recoveryRef = useRef(recovery)
  recoveryRef.current = recovery
  const authorRef = useRef(author)
  authorRef.current = author
  const pausedRef = useRef(paused)
  pausedRef.current = paused
  // Timers are off for a PUBLISHED article unless the caller opts in: the backend
  // applies edits to a published article immediately, so a 2-second autosave would
  // push half-finished edits live. Local recovery snapshots still run.
  const autoSaveEnabledRef = useRef(autoSaveEnabled)
  autoSaveEnabledRef.current =
    autoSaveEnabled && (autoSavePublished || editorState.publishedStatus !== 'published')
  const callbacksRef = useRef({ onCreated, onError, onSuccess })
  callbacksRef.current = { onCreated, onError, onSuccess }

  const changeCounterRef = useRef(0)
  const lastSavedCounterRef = useRef(0)
  const lastSavedValuesRef = useRef<ArticleFormValues | null>(null)
  const savingRef = useRef(false)
  /** The publish currently running, so repeated clicks share one request. */
  const publishPromiseRef = useRef<Promise<BlogSingleResponse | null> | null>(null)
  const conflictPausedRef = useRef(false)
  const createAmbiguousRef = useRef(false)
  const initialisedRef = useRef(false)
  const hydratedSerializedRef = useRef<string | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const runSaveRef = useRef<(manual: boolean) => Promise<boolean>>(async () => false)

  const isDirty = (): boolean => changeCounterRef.current !== lastSavedCounterRef.current

  const clearDebounce = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = null
  }

  const scheduleDebouncedSave = useCallback(() => {
    clearDebounce()
    debounceTimerRef.current = setTimeout(() => {
      void runSaveRef.current(false)
    }, DEBOUNCE_MS)
  }, [])

  const applyServerArticle = (response: BlogSingleResponse) => {
    const next: ArticleEditorState = {
      slug: response.slug,
      version: response.version,
      publishedStatus: response.publishedStatus,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
    }
    editorStateRef.current = next
    setEditorState(next)
  }

  /** Marks `requestValues` (captured when the request started) as saved, without touching edits made since. */
  const markSaved = (requestValues: ArticleFormValues, requestCounter: number) => {
    lastSavedValuesRef.current = requestValues
    lastSavedCounterRef.current = requestCounter

    if (changeCounterRef.current === requestCounter) {
      recoveryRef.current.clear()
      setSaveStatus({ state: 'saved', at: new Date().toISOString() })
    } else {
      // Edits arrived while the request was in flight: keep their snapshot, stay dirty, save again.
      setSaveStatus({ state: 'unsaved' })
      if (autoSaveEnabledRef.current) scheduleDebouncedSave()
    }
  }

  const handleConflict = async (slug: string) => {
    conflictPausedRef.current = true
    clearDebounce()
    // Copy the admin's work somewhere later typing can't overwrite it.
    recoveryRef.current.preserveForConflict(
      editorStateRef.current.version,
      changeCounterRef.current,
      valuesRef.current,
    )
    try {
      const res = await blogService.getSingle(slug)
      setConflict({ latestArticle: res.data.data })
      setSaveStatus({ state: 'conflict' })
    } catch {
      setSaveStatus({
        state: 'error',
        message:
          'This article was changed elsewhere and the latest version could not be loaded. Your changes are stored locally. Reload the page to continue.',
      })
      callbacksRef.current.onError?.('Could not load the latest version of this article.')
    }
  }

  /** Shared core for autosave, Save Draft, Publish and Preview. Never run two at once. */
  const performSave = useCallback(
    async (opts: { publish: boolean; manual: boolean }): Promise<SaveOutcome> => {
      if (pausedRef.current || conflictPausedRef.current || savingRef.current) return { ok: false }
      if (!opts.publish && !isDirty()) return { ok: true, response: null }

      const requestCounter = changeCounterRef.current
      const requestValues = valuesRef.current
      const current = editorStateRef.current
      const creating = !current.slug

      if (!requestValues.title.trim()) {
        setSaveStatus({
          state: 'error',
          message: 'Add a title to save this draft. Your changes are stored locally.',
        })
        return { ok: false }
      }
      if (!creating && current.version === null) return { ok: false }
      // A create whose response may have been lost must not be retried automatically.
      if (creating && createAmbiguousRef.current && !opts.manual) return { ok: false }

      savingRef.current = true
      setSaveStatus({ state: 'saving' })

      try {
        let response: BlogSingleResponse

        if (creating) {
          const { payload, unsupportedNodeTypes } = buildCreateDraftPayload(requestValues, {
            author: authorRef.current,
          })
          const problem = describeProblems(unsupportedNodeTypes, payload.content)
          if (problem) {
            setSaveStatus({ state: 'error', message: problem })
            return { ok: false }
          }
          if (opts.publish) payload.publishedStatus = 'published'

          const res = await blogService.create(payload)
          response = res.data.data
          createAmbiguousRef.current = false
          callbacksRef.current.onCreated?.(response.slug)
        } else {
          const { payload, unsupportedNodeTypes } = buildUpdatePayload(
            requestValues,
            lastSavedValuesRef.current,
            {
              version: current.version as number,
              author: authorRef.current,
              publishedStatus: opts.publish ? 'published' : undefined,
            },
          )
          const problem = describeProblems(unsupportedNodeTypes, payload.content)
          if (problem) {
            setSaveStatus({ state: 'error', message: problem })
            return { ok: false }
          }
          if (!opts.publish && !hasPayloadChanges(payload)) {
            // The editor re-normalised its content; nothing actually differs from the server.
            markSaved(requestValues, requestCounter)
            return { ok: true, response: null }
          }

          const res = await blogService.update(current.slug as string, payload)
          response = res.data.data
        }

        applyServerArticle(response)
        markSaved(requestValues, requestCounter)
        return { ok: true, response }
      } catch (error) {
        if (getHttpStatus(error) === 409 && current.slug) {
          await handleConflict(current.slug)
          return { ok: false }
        }

        if (creating && getHttpStatus(error) === undefined) {
          // Timeout / network drop: the server may have created the draft. Do not auto-retry.
          createAmbiguousRef.current = true
          setSaveStatus({
            state: 'error',
            message:
              'Could not confirm the draft was created. Your changes are stored locally. Check the article list before saving again.',
          })
          callbacksRef.current.onError?.(
            'Could not confirm the draft was created. Check the article list before saving again.',
          )
          return { ok: false }
        }

        const message = getErrorMessage(error, opts.publish ? 'Publish failed.' : 'Save failed.')
        setSaveStatus({
          state: 'error',
          message: `Unable to save: ${message} Changes are stored locally.`,
        })
        if (opts.manual || opts.publish) callbacksRef.current.onError?.(message)
        return { ok: false }
      } finally {
        savingRef.current = false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const runSave = useCallback(
    async (manual: boolean): Promise<boolean> => (await performSave({ publish: false, manual })).ok,
    [performSave],
  )
  runSaveRef.current = runSave

  // Max-interval guarantee: independent of the debounce, fires at most every
  // ~20s and only when something is actually dirty.
  useEffect(() => {
    const timer = setInterval(() => {
      if (autoSaveEnabledRef.current && isDirty()) void runSaveRef.current(false)
    }, MAX_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => () => clearDebounce(), [])

  // Change tracking. Keyed on a serialized copy so a new object identity with
  // identical content never counts as an edit.
  const serialized = useMemo(() => fingerprintArticleValues(values), [values])

  useEffect(() => {
    // Values just loaded from the server (hydrate / reload): that is the saved baseline, not an edit.
    if (hydratedSerializedRef.current !== null && hydratedSerializedRef.current === serialized) {
      hydratedSerializedRef.current = null
      lastSavedValuesRef.current = valuesRef.current
      lastSavedCounterRef.current = changeCounterRef.current
      return
    }
    // The very first run is the initial blank form, not an edit.
    if (!initialisedRef.current) {
      initialisedRef.current = true
      lastSavedValuesRef.current = valuesRef.current
      return
    }

    changeCounterRef.current += 1
    recoveryRef.current.persistSnapshot(
      editorStateRef.current.version,
      changeCounterRef.current,
      valuesRef.current,
    )

    if (conflictPausedRef.current) return
    setSaveStatus((previous: any) =>
      previous.state === 'saving' ? previous : { state: 'unsaved' },
    )
    if (autoSaveEnabledRef.current && !pausedRef.current) scheduleDebouncedSave()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized])

  // --- Public actions ------------------------------------------------------

  /**
   * Loads server data as the saved baseline. Call it right next to the form
   * `reset(...)` that puts the same values into the form.
   */
  const hydrate = useCallback((hydratedValues: ArticleFormValues, state: ArticleEditorState) => {
    hydratedSerializedRef.current = fingerprintArticleValues(hydratedValues)
    editorStateRef.current = state
    setEditorState(state)
    lastSavedValuesRef.current = hydratedValues
    lastSavedCounterRef.current = changeCounterRef.current
    setSaveStatus({ state: 'idle' })
  }, [])

  /** Save Draft button / "Save changes" on a published article. Saves immediately. */
  const saveNow = useCallback(async (): Promise<boolean> => {
    clearDebounce()
    while (savingRef.current) await delay(75)
    return runSave(true)
  }, [runSave])

  /**
   * Publish: waits for any in-flight save, then sends pending edits and
   * `publishedStatus: 'published'` in ONE PUT (or one POST for a never-saved
   * article) using the latest confirmed version. Returns the saved article,
   * or null on failure / conflict. Validate with the publish schema first.
   */
  const publish = useCallback((): Promise<BlogSingleResponse | null> => {
    // A second click while a publish is running joins it instead of sending another PUT.
    if (publishPromiseRef.current) return publishPromiseRef.current
    clearDebounce()
    const run = (async (): Promise<BlogSingleResponse | null> => {
      while (savingRef.current) await delay(75)
      const outcome = await performSave({ publish: true, manual: true })
      if (outcome.ok && outcome.response) {
        callbacksRef.current.onSuccess?.('Article published')
        return outcome.response
      }
      return null
    })()
    publishPromiseRef.current = run
    const release = () => {
      publishPromiseRef.current = null
    }
    run.then(release, release)
    return run
  }, [performSave])

  /** Sets the article back to draft using the latest confirmed version. */
  const unpublish = useCallback(async (): Promise<BlogSingleResponse | null> => {
    clearDebounce()
    while (savingRef.current) await delay(75)
    const current = editorStateRef.current
    if (!current.slug || current.version === null || conflictPausedRef.current) return null

    savingRef.current = true
    try {
      const res = await blogService.update(current.slug, buildUnpublishPayload(current.version))
      applyServerArticle(res.data.data)
      callbacksRef.current.onSuccess?.('Article unpublished')
      return res.data.data
    } catch (error) {
      if (getHttpStatus(error) === 409) {
        await handleConflict(current.slug)
      } else {
        callbacksRef.current.onError?.(getErrorMessage(error, 'Failed to unpublish the article.'))
      }
      return null
    } finally {
      savingRef.current = false
    }
  }, [])

  /**
   * Makes sure the saved copy is current and returns the slug (for Preview).
   * Returns null if the article can't be saved (no title yet, error,
   * conflict, paused).
   */
  const ensureSaved = useCallback(async (): Promise<string | null> => {
    clearDebounce()
    while (savingRef.current) await delay(75)
    if (pausedRef.current || conflictPausedRef.current) return null
    if (isDirty() || !editorStateRef.current.slug) {
      const outcome = await performSave({ publish: false, manual: true })
      if (!outcome.ok) return null
    }
    return editorStateRef.current.slug
  }, [performSave])

  // --- Conflict resolution -------------------------------------------------

  /**
   * "Reload latest version": returns the server's content for the caller to
   * `reset()` the form with. The local copy of the admin's work is kept (and
   * offered again via the recovery prompt) until an explicit discard.
   */
  const reloadLatest = useCallback(() => {
    if (!conflict) return null
    const result = blogResponseToForm(conflict.latestArticle)
    hydrate(result.values, result.editorState)
    setConflict(null)
    conflictPausedRef.current = false
    setSaveStatus({ state: 'idle' })
    recoveryRef.current.checkForRecovery(result.values)
    return result
  }, [conflict, hydrate])

  /**
   * "Restore my unsaved changes": keeps the form as it is and adopts the
   * server's current version number so the next save targets it instead of the
   * stale one that caused the 409. Only fields YOU changed since your last
   * successful save are sent, so fields you never touched keep the other
   * admin's values. Fields you both edited take your value.
   */
  const restoreMine = useCallback(() => {
    if (!conflict) return
    const next: ArticleEditorState = {
      slug: conflict.latestArticle.slug,
      version: conflict.latestArticle.version,
      publishedStatus: conflict.latestArticle.publishedStatus,
      createdAt: conflict.latestArticle.createdAt,
      updatedAt: conflict.latestArticle.updatedAt,
    }
    editorStateRef.current = next
    setEditorState(next)
    setConflict(null)
    conflictPausedRef.current = false
    setSaveStatus({ state: 'unsaved' })
    if (autoSaveEnabledRef.current) scheduleDebouncedSave()
  }, [conflict, scheduleDebouncedSave])

  /** "Discard my changes": loads the server version and deletes the local copies. Only call after an explicit confirmation. */
  const discardMine = useCallback(() => {
    const result = reloadLatest()
    recoveryRef.current.discard()
    return result
  }, [reloadLatest])

  return {
    editorState,
    saveStatus,
    conflict,
    hydrate,
    saveNow,
    publish,
    unpublish,
    ensureSaved,
    reloadLatest,
    restoreMine,
    discardMine,
    recovery,
    /** Accurate at call time (use it in beforeunload handlers). */
    getIsDirty: isDirty,
    getIsSaving: () => savingRef.current,
  }
}

/** Turns adapter/limit problems into one message, or null when the content is fine to send. */
const describeProblems = (
  unsupportedNodeTypes: string[],
  content: BackendContentBlock[] | undefined,
): string | null => {
  if (unsupportedNodeTypes.length > 0) {
    return `This content includes ${unsupportedNodeTypes.join(', ')}, which cannot be saved. Remove it to continue. Your changes are stored locally.`
  }
  if (content) {
    const issues = validateBlocks(content)
    if (issues.length > 0) return `${issues[0]} Your changes are stored locally.`
  }
  return null
}
